/**
 * "Back" buttons that return to the screen the user came from.
 *
 * `router.back()` is wrong on web for screens outside (tabs) (the report,
 * progress details, activation, admin pages): it pops the root Slot, whose
 * initial route is (public), so it landed on "/" (Home, with the tab state
 * reset) or, after a direct load from an email or a link, on "/dashboard".
 * The browser history holds exactly the URL the user came from, so a Back
 * button walks it when the previous entry is this app's. Each entry carries
 * its in-app depth in `history.state` (0 for the entry the app was opened
 * on), so a direct load never sends the user back out of the app.
 *
 * Kept free of react-native / expo imports so it stays unit-testable.
 */
const DEPTH_KEY = '__mcaiDepth';

interface HistoryLike {
  readonly state: unknown;
  pushState(data: unknown, unused: string, url?: string | URL | null): void;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
  back(): void;
}

interface WindowLike {
  history: HistoryLike;
  addEventListener(type: 'popstate', listener: () => void): void;
}

interface RouterLike {
  replace(href: never): void;
  back(): void;
  canGoBack?: () => boolean;
}

let trackedWindow: WindowLike | null = null;
let depth = 0;

function depthOf(state: unknown): number | null {
  const value = state && typeof state === 'object' ? (state as Record<string, unknown>)[DEPTH_KEY] : undefined;
  return typeof value === 'number' ? value : null;
}

function withDepth(state: unknown, value: number): unknown {
  return state && typeof state === 'object'
    ? { ...(state as Record<string, unknown>), [DEPTH_KEY]: value }
    : { [DEPTH_KEY]: value };
}

/** Start counting in-app history depth. Call once, at app start, on web. */
export function installInAppHistory(win: WindowLike | undefined): void {
  if (!win?.history || trackedWindow === win) return;
  trackedWindow = win;
  const history = win.history;
  // A reload keeps the depth stored on the current entry.
  depth = depthOf(history.state) ?? 0;
  const push = history.pushState.bind(history);
  const replace = history.replaceState.bind(history);
  history.pushState = (data, unused, url) => {
    depth += 1;
    push(withDepth(data, depth), unused, url);
  };
  history.replaceState = (data, unused, url) => {
    replace(withDepth(data, depth), unused, url);
  };
  win.addEventListener('popstate', () => {
    depth = depthOf(history.state) ?? 0;
  });
  replace(withDepth(history.state, depth), '');
}

/** True when the previous browser history entry is a screen of this app. */
export function canGoBackInApp(): boolean {
  return trackedWindow !== null && depth > 0;
}

/**
 * Back to the previous in-app screen; without one (direct load, link from an
 * email), replace with `fallback`.
 */
export function goBackOr(router: RouterLike, fallback: string): void {
  if (trackedWindow) {
    if (depth > 0) trackedWindow.history.back();
    else router.replace(fallback as never);
    return;
  }
  // Native: the stack navigator's own history is correct.
  if (router.canGoBack?.()) router.back();
  else router.replace(fallback as never);
}

/** Test hook: forget the tracked window. */
export function resetInAppHistoryForTests(): void {
  trackedWindow = null;
  depth = 0;
}
